import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/**
 * Reusable server-side pagination control.
 *
 * Props:
 *   currentPage        {number}   - Active page (1-based)
 *   totalItems         {number}   - Total record count from the server
 *   itemsPerPage       {number}   - Records per page
 *   onPageChange       {function} - Called with the new page number
 *   onItemsPerPageChange {function} - Called with the new limit value
 *   pageSizeOptions    {number[]} - Options for the per-page selector (default: [5,10,25,50])
 *   itemLabel          {string}   - Noun used in "Showing X to Y of Z <itemLabel>" (default: "items")
 *   currentPageCount   {number}   - Number of items on the current page (defaults to itemsPerPage)
 */
export default function Pagination({
  currentPage,
  totalItems,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  pageSizeOptions = [5, 10, 25, 50],
  itemLabel = "items",
  currentPageCount,
}) {
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const pageCount = currentPageCount ?? Math.min(itemsPerPage, totalItems - (currentPage - 1) * itemsPerPage);
  const from = Math.min((currentPage - 1) * itemsPerPage + 1, totalItems);
  const to = Math.min((currentPage - 1) * itemsPerPage + pageCount, totalItems);

  const buildPageButtons = () => {
    const maxButtons = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxButtons / 2));
    const endPage = Math.min(totalPages, startPage + maxButtons - 1);

    if (endPage - startPage + 1 < maxButtons) {
      startPage = Math.max(1, endPage - maxButtons + 1);
    }

    const pages = [];

    if (startPage > 1) {
      pages.push(
        <Button
          key="1"
          variant={currentPage === 1 ? "default" : "ghost"}
          size="sm"
          onClick={() => onPageChange(1)}
          className="h-8 w-8 p-0 rounded-none"
        >
          1
        </Button>
      );
      if (startPage > 2) {
        pages.push(
          <Button key="ellipsis1" variant="ghost" size="sm" disabled className="h-8 w-8 p-0 rounded-none">
            ...
          </Button>
        );
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <Button
          key={i}
          variant={currentPage === i ? "default" : "ghost"}
          size="sm"
          onClick={() => onPageChange(i)}
          className="h-8 w-8 p-0 rounded-none"
        >
          {i}
        </Button>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(
          <Button key="ellipsis2" variant="ghost" size="sm" disabled className="h-8 w-8 p-0 rounded-none">
            ...
          </Button>
        );
      }
      pages.push(
        <Button
          key={totalPages}
          variant={currentPage === totalPages ? "default" : "ghost"}
          size="sm"
          onClick={() => onPageChange(totalPages)}
          className="h-8 w-8 p-0 rounded-none"
        >
          {totalPages}
        </Button>
      );
    }

    return pages;
  };

  if (totalItems === 0) return null;

  return (
    <div className="p-4 border-t border-gray-200 bg-gray-50 flex flex-col sm:flex-row justify-between items-center gap-4">
      <div className="text-sm text-gray-600">
        Showing {from} to {to} of {totalItems} {itemLabel}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Select
          value={String(itemsPerPage)}
          onValueChange={(value) => {
            onItemsPerPageChange(Number(value));
            onPageChange(1);
          }}
        >
          <SelectTrigger className="w-[80px]">
            <SelectValue placeholder={String(itemsPerPage)} />
          </SelectTrigger>
          <SelectContent>
            {pageSizeOptions.map((size) => (
              <SelectItem key={size} value={String(size)}>
                {size}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex items-center">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(Math.max(currentPage - 1, 1))}
            disabled={currentPage === 1}
            className="h-8 px-2 rounded-r-none"
          >
            Previous
          </Button>

          <div className="flex items-center border-y border-x">
            {buildPageButtons()}
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(Math.min(currentPage + 1, totalPages))}
            disabled={currentPage === totalPages || totalItems === 0}
            className="h-8 px-2 rounded-l-none"
          >
            Next
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm">Go to:</span>
          <Input
            type="number"
            min="1"
            max={totalPages}
            value={currentPage}
            onChange={(e) => {
              const value = Number.parseInt(e.target.value);
              if (!isNaN(value) && value >= 1 && value <= totalPages) {
                onPageChange(value);
              }
            }}
            className="w-16 h-8"
          />
        </div>
      </div>
    </div>
  );
}
